import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import styled from 'styled-components'
import HeroImage from 'content/components/HeroImage'
import Group from 'components/utils/Group'
import { renderMarkdown } from 'lib/string/markdownUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import { getThemeClassName } from 'lib/theme/themeUtils'
import TextInput from 'components/Common/Form/Input/TextInput'
import LineCopyIcon from 'LuxKit/Icons/line/LineCopyIcon'
import { copyValueToClipboard } from 'lib/web/clipboardUtils'
import Clickable from 'components/Common/Clickable'
import Image from 'components/Common/Image'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import Label from 'LuxKit/Label'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LayoutContainer from 'components/Common/LayoutContainer'

export function labelMapping(label: App.ContentfulCampaignHeroLabels): React.ReactElement | null {
  switch (label) {
    case 'special-offer':
      return <Label key={label} kind="primary" variant="highlight-primary-dark" startIcon={<SolidFireIcon />}>
        <FormattedMessage
          id="aVBhQPu45Yn4C-oRHKgQ"
          defaultMessage="Special Offer"
          
        />
      </Label>
    case 'offer-ends-soon':
      return <MessagingLabel key={label} variant="urgency">
        <FormattedMessage
          id="CbRw6W_xtguX0iKUN7xk"
          defaultMessage="Offer Ends Soon"
          
        />
      </MessagingLabel>
    default:
      return null
  }
}

const HeroContainer = styled(HeroImage)`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  &.background-color-white {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  }

  &.background-color-grey {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  }
  align-items: flex-start;
  justify-content: flex-end;

  ${mediaQueryUp.tablet} {
    justify-content: center;
  }
`

const HeroDimmedOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(0deg, #363A45 36%,rgba(54, 58, 69, 0.52) 55% , rgba(54, 58, 69, 0.50) 55%, rgba(54, 58, 69, 0.00) 60%);
  pointer-events: none;

  ${mediaQueryUp.tablet} {
    background: linear-gradient(90deg, #363A45 36%, rgba(54, 58, 69, 0.52) 55%, rgba(54, 58, 69, 0.50) 55%, rgba(54, 58, 69, 0.00) 60%);
  }
`

const HeroContent = styled(Group)`
  position: relative;

  ${mediaQueryUp.tablet} {
    max-width: ${rem(440)}
  }
`

function ContentfulCampaignHero(props: App.ComponentCampaignHero) {
  const intl = useIntl()
  const bodyText = props.bodyText ? renderMarkdown(props.bodyText) : undefined
  const footerText = props.footerText ? renderMarkdown(props.footerText) : undefined
  const [copied, setCopied] = useState(false)
  const contentfulLogoAltText = intl.formatMessage({
    id: 'UqJACPKBc0XJg4-v3riR',
    defaultMessage: 'Campaign logo',
  })
  const copyToClipBoardHandler = () => {
    copyValueToClipboard(props.promoCode!)
    setCopied(true)
    setTimeout(() => {
      setCopied(false)
    },
    1500)
  }
  return (
    <HeroContainer
      alt={props.title}
      fit="center"
      gravity="center"
      id={props.backGroundImage.id!}
      mobileAspectRatio="8:9"
      tabletAspectRatio="10:3"
      className={getThemeClassName({ mobile: 'inverse' })}
    >
      <HeroDimmedOverlay aria-hidden="true" role="presentation" />
      <LayoutContainer size="xl">
        <HeroContent direction="vertical" gap={12} tabletGap={12} horizontalAlign="start">
          <Group fullHeight direction="vertical" tabletDirection="horizontal" gap={8} horizontalAlign="center" tabletHorizontalAlign="start" tabletVerticalAlign="center" verticalAlign="end">
            {props.logoImageId?.id && <Image
              id={props.logoImageId.id}
              alt={contentfulLogoAltText}
              style={{
                width: 120,
                padding: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />}
            <LabelGroup>
              {props.labels?.map(labelMapping)}
            </LabelGroup>
          </Group>
          <Heading colour="normal-contrast" variant="heading2" dangerouslySetInnerHTML={{ __html: props.title }}></Heading>
          {bodyText && (
            <BodyText
              colour="light-foreground-alt"
              variant="medium"
              dangerouslySetInnerHTML={{ __html: bodyText }}
            />
          )}
          {
            props.promoCode && (
              <TextInput
                value={props.promoCode}
                helpText={intl.formatMessage({
                  id: 'JSF-xpElIWF7ht2HjCZm',
                  defaultMessage: 'Use this promo code at checkout.',
                })}
                endIcon={
                  <>
                    {!copied && <Clickable onClick={copyToClipBoardHandler}>
                      <LineCopyIcon />
                    </Clickable>}
                    {copied && <LineCheckIcon />}
                  </>
                }
              />
            )
          }
          {footerText && (
            <BodyText
              colour="light-foreground-alt"
              variant="medium"
              dangerouslySetInnerHTML={{ __html: footerText }}
            />
          )}
        </HeroContent>
      </LayoutContainer>
    </HeroContainer>
  )
}

export default ContentfulCampaignHero
